import React, { useEffect, useState } from 'react';
import moment, { Moment } from 'moment';
import Calendar, { CalendarSettings } from './Calendar';
import CalendarPatientEventModal from './CalendarPatientEventModal';
import apiClient, { IResponse } from '../../Services/ApiClient';
import { EVENT } from '../../Modules/Events/Graphql/events.graphql';
import { workplaceWorkingHours } from '../../Modules/Workplace/Services/workplace-working-hours.service';
import { useAppContext } from '../../Services/AppContext';
import AppointmentDetailModal from '../HealthPlan/Appointment/AppointmentDetailModal';
import { useEffectAsync } from '../../helpers';
import CalendarContextMenu from './CalendarContextMenu';
import HealthPlanCreateConfirmModal from '../../Modules/HealthPlan/Components/HealthPlanCreateConfirm/HealthPlanCreateConfirmModal';
import useTranslation from '../../Hooks/useTranslation';
import { useHealthPlanContext } from '../../Services/HealthPlanContext';
import { Flyer } from '../../Modules/EArchive/Enums/Flyer.enum';
import { HealthPlanCode } from '../../Enums/HealthPlanCode.enum';
import { CALENDAR_EVENT_TYPE } from './CalendarEvent/CalendarEvent';
import CalendarEventModal from './CalendarEvent/CalendarEventModal';
import { getMomentDate } from '../../Utils/dates.util';
import { WorkplaceCalendarWithFilterValues } from '../../Modules/Calendar/Components/WorkplaceCalendar/WorkplaceCalendarFilter';
import {
	EventMemberState,
	ServiceScheduleType,
	useLazyWorkplaceEventsQuery,
} from '../../graphql/graphql';
import useServiceSchedules from '../../Modules/Workplace/Hooks/useServiceSchedules';
import useHolidays from '../../Modules/Calendar/Hooks/useHolidays';
import { CalendarView } from '../../Modules/Calendar/Enums/CalendarView.eunm';

interface IWorkplaceCalendar {
	workplaceId: string;
	patientId?: string;
	caseId?: string;
	params?: any;
	onEventIsCreated?: (response: IResponse) => void;
	isNurseView?: boolean;
	healthPlanCode?: HealthPlanCode;
	calendarEventType?: CALENDAR_EVENT_TYPE;
	setIsLoading?: (isLoading: boolean) => void;
	filter?: WorkplaceCalendarWithFilterValues;
	onShowOnyMeChange?: (state: boolean) => void;
	preselectedDate?: Moment;
	settings?: CalendarSettings;
	onViewChange?: (view: CalendarView) => void;
}

export default function WorkplaceCalendar(props: IWorkplaceCalendar) {
	const {
		patientId,
		caseId,
		params,
		onEventIsCreated,
		isNurseView,
		healthPlanCode,
		calendarEventType,
		filter,
		onShowOnyMeChange,
		preselectedDate,
		settings,
		onViewChange,
	} = props;
	const workplaceId = filter?.workplaceId
		? filter.workplaceId
		: props.workplaceId;
	const { t } = useTranslation();
	const { healthPlanUser } = useHealthPlanContext();
	const [ignoreParams, setIgnoreParams] = useState(false);

	const [detailOpened, setDetailOpened] = useState(false);
	const [selectedDate, setSelectedDate] = useState(null);
	const [isNurseConfirmModalOpened, setIsNurseConfirmModalOpened] =
		useState(false);
	const [selectedEvent, setSelectedEvent] = useState(null);
	const [moveToDate, setMoveToDate] = useState(null);
	const [scrollToEventId, setScrollToEventId] = useState<string | undefined>(
		undefined,
	);
	const [dateRange, setDateRange] = useState({ start: null, end: null });
	const [events, setEvents] = useState([]);
	const [loadingEvents, setLoadingEvents] = useState(false);
	const [businessHours, setBusinessHours] = useState([]);
	const [contextMenuProps, setContextMenuProps] = useState({
		isOpen: false,
	});
	const [showMyEvents, setShowMyEvents] = useState(false);
	const { userData, translation } = useAppContext();
	const [getWorkplaceEvents] = useLazyWorkplaceEventsQuery();
	const {
		getSchedulesAsCollapseAbleEvents,
		isLoading: isLoadingServiceSchedule,
	} = useServiceSchedules();
	const { getHolidaysAsFullDayEvents } = useHolidays();

	const showLoading = (isLoading: boolean) => {
		if (props.setIsLoading) {
			props.setIsLoading(isLoading);
		} else {
			setLoadingEvents(isLoading);
		}
	};

	useEffect(() => {
		if (preselectedDate) {
			setMoveToDate(preselectedDate.toISOString());
		}
	}, [preselectedDate]);

	useEffect(() => {
		const selectedOnlyMe =
			filter?.doctorIds?.length === 1 &&
			filter?.doctorIds?.some(item => item === userData.id);
		setShowMyEvents(!!selectedOnlyMe);
	}, [filter]);

	useEffect(() => {
		onShowOnyMeChange?.(showMyEvents);
	}, [showMyEvents]);

	// @ts-ignore
	useEffectAsync(async () => {
		showLoading(true);
		await handleLoadData();
		await handleLoadWorkingHours();
	}, [dateRange, showMyEvents, filter]);

	useEffect(() => {
		showLoading(false);
	}, [events]);

	const handlePickDate = date => {
		setSelectedDate(date);
		setDetailOpened(true);
	};

	const handleEventClick = event => {
		setSelectedEvent(event);
		setContextMenuProps({
			isOpen: true,
			clientX: event.jsEvent.clientX,
			clientY: event.jsEvent.clientY,
		});

		for (const divItem of event.el.querySelectorAll('[aria-describedby]')) {
			const attribute = divItem.getAttribute('aria-describedby');
			if (attribute.includes('popup')) {
				// @ts-ignore
				const popup = document.getElementById(attribute);
				if (popup) {
					popup.style.display = 'none';
				}
			}
		}
	};

	const handleDateChange = (start: Moment, end?: Moment) => {
		setDateRange({ start, end });
	};

	const handleClose = () => {
		setSelectedEvent(null);
		setDetailOpened(false);
		handleLoadData();
	};

	const handleCloseWithoutReload = () => {
		setSelectedEvent(null);
		setDetailOpened(false);
	};

	const handleAfterNewEventCreated = (response: IResponse) => {
		handleLoadData();
		if (isNurseView && response?.data?.isFirstMeeting) {
			setIsNurseConfirmModalOpened(true);
		}
	};

	const handleAfterEventChanged = () => {
		setSelectedEvent(null);
		handleLoadData();
	};

	// @ts-ignore
	const handleLoadWorkingHours = async () => {
		const a = await workplaceWorkingHours({ workplaceId });

		setBusinessHours(a);
	};

	const handleLoadData = () => {
		if (!dateRange.start) {
			return;
		}

		// @ts-ignore
		const a = async () => {
			showLoading(true);

			const tempEvents = [];

			const variables = {
				start: dateRange.start.format(),
				end: (dateRange?.end || dateRange.start).format(),
				types: ['DEFAULT', 'RESERVATION'],
				workplaceIds: [workplaceId],
				userIds: showMyEvents
					? [userData.id]
					: filter?.doctorIds?.length
					? filter?.doctorIds
					: undefined,
			};

			const workplaceEvents = await getWorkplaceEvents(variables);

			for (const event of workplaceEvents?.data?.workplaceEvents?.data || []) {
				let isConfirmedByPatient = false;
				let isChild = false;
				let patientsName;
				let patientPhone;
				let patientBirthNumber;
				let patientAge;
				let patientId;
				let doctor: { id?: string; name?: string } = {};
				event.eventMembers.forEach((user, index) => {
					if (user?.user?.isPatient) {
						patientId = user?.user?.id;
						patientsName = user?.user?.profile?.fullName;
						patientAge = user?.user?.profile?.age;
						patientBirthNumber = user?.user?.profile?.birthNumber;
						patientPhone = user?.user?.formattedPhoneNumber;
						isConfirmedByPatient = user?.state === EventMemberState.Confirmed;
					}
					if (user?.user?.isChild) {
						patientId = user?.user?.id;
						patientsName = user?.user?.profile?.fullName;
						patientAge = user?.user?.profile?.age;
						patientBirthNumber = user?.user?.profile?.birthNumber;
						patientPhone = user?.user?.formattedPhoneNumber;
						isChild = true;
					}
					if (user?.user?.isDoctor) {
						if (!doctor.name) {
							doctor.name = user?.user?.profile?.fullName;
							doctor.id = user?.user?.id;
						}
					}
				});
				tempEvents.push({
					id: event.id,
					title: event.title,
					doctor,
					patient: {
						id: patientId ? patientId : '',
						name: patientsName ? patientsName : '',
						age: patientAge ? patientAge : '',
						birthNumber: patientBirthNumber ? patientBirthNumber : '',
						phone: patientPhone ? patientPhone : '',
						isChild,
					},
					workplace: event.calendar.workplace.doctorDisplayName ?? null,
					workplaceId: event.calendar.workplaceId ?? null,
					start: moment(event.dateFrom).format(),
					end: moment(event.dateTo).format(),
					backgroundColor: event.layout.backgroundColor,
					borderColor: event.layout.backgroundColor,
					textColor: event.layout.fontColor,
					resourceId: event.calendar.workplaceId ?? null,
					note: event.description,
					meta: event.meta,
					createdByPatient: event.createdBy?.isPatient,
					caseId: event.caseId,
					isConfirmedByPatient,
				});
			}

			const scheduleEvents = await getSchedulesAsCollapseAbleEvents({
				from: getMomentDate(dateRange.start),
				to: getMomentDate(dateRange.end || dateRange.start),
				workplaceIds: [workplaceId],
				type: ServiceScheduleType.Shift,
			});
			scheduleEvents.map(t => tempEvents.push(t));

			const scheduleEvents2 = await getSchedulesAsCollapseAbleEvents({
				from: getMomentDate(dateRange.start),
				to: getMomentDate(dateRange.end || dateRange.start),
				workplaceIds: [workplaceId],
				type: ServiceScheduleType.ShiftExtra,
			});
			scheduleEvents2.map(t => tempEvents.push(t));

			const tempEvents4 = getHolidaysAsFullDayEvents();
			tempEvents4.map(t => tempEvents.push(t));

			setEvents(tempEvents);
		};
		a();
	};

	const handleEventDrop = event => {
		// @ts-ignore
		const a = async () => {
			await apiClient.patch(`/event/${event.id}`, {
				dateFrom: moment(event.start).format(),
				dateTo: moment(event.end).format(),
				propIgnoreFreeTimeCheck: true,
				workplaceId,
			});
		};

		a();
	};

	// @ts-ignore
	const handleRemoveEvent = async function (event) {
		await apiClient.delete('/events/cancel/' + event.event.id);
		setSelectedEvent(null);
		handleLoadData();
	};

	const handleEditEvent = () => {
		setDetailOpened(true);
	};

	const handleCancelRemove = () => {
		setSelectedEvent(null);
	};

	const handleCreateNewEvent = (date: Moment) => {
		setSelectedEvent(null);
		handlePickDate(getMomentDate(date));
	};

	const handleAfterMarkDone = () => {
		setSelectedEvent(null);
		setContextMenuProps({ isOpen: false });
		handleLoadData();
	};

	// go to date by eventId
	// @ts-ignore
	useEffectAsync(async () => {
		if (params?.eventId) {
			const { data } = await apiClient.graphql({
				query: EVENT,
				variables: {
					id: params.eventId,
				},
				fetchPolicy: 'network-only',
			});
			if (data?.event?.dateFrom) {
				setMoveToDate(data.event.dateFrom);
				setScrollToEventId(data.event.id);
			}
		}
		if (params?.createNewEvent) {
			handlePickDate(moment());
		}
	}, [params]);

	const eventDidMount = () => {
		if (params?.autoOpen && !ignoreParams) {
			// @ts-ignore
			const element = document.getElementById('event-' + params.eventId);
			setTimeout(() => {
				element.click();
				setContextMenuProps({ isOpen: false });
				setDetailOpened(true);
				setIgnoreParams(true);
			}, 100);
		}
	};

	return (
		<>
			<Calendar
				events={events}
				onTimeClick={handlePickDate}
				onEventClick={handleEventClick}
				initialView={'timeGridWeek'}
				onDateChange={handleDateChange}
				allDaySlot={true}
				businessHours={businessHours}
				editable={true}
				onEventDrop={handleEventDrop}
				onEventResize={handleEventDrop}
				loadingEvents={loadingEvents || isLoadingServiceSchedule}
				moveToDate={moveToDate}
				showMyEvents={showMyEvents}
				eventDidMount={eventDidMount}
				scrollToEventId={scrollToEventId}
				settings={settings}
				onViewChange={onViewChange}
			/>
			{selectedEvent && (
				<CalendarContextMenu
					allowShowPatientProfile={!patientId}
					contextParams={contextMenuProps}
					event={selectedEvent}
					onEdit={() => handleEditEvent()}
					onRemove={handleRemoveEvent}
					onRemoveCancel={handleCancelRemove}
					onCreateNewEvent={handleCreateNewEvent}
					removeConfirmationTitle={'component.appointment.cancel.title'}
					removeConfirmationText={'component.appointment.cancel.text'}
					onContextClose={() => {
						setContextMenuProps({ isOpen: false });
					}}
					onMarkEventDone={handleAfterMarkDone}
				/>
			)}
			{calendarEventType && (
				<CalendarEventModal
					isOpened={detailOpened}
					setIsOpened={setDetailOpened}
					onEventCreate={handleAfterNewEventCreated}
					onEventEdit={handleAfterEventChanged}
					type={calendarEventType}
					onClose={handleCloseWithoutReload}
					params={{
						patientId: patientId,
						caseId: caseId,
						healthPlanCode: healthPlanCode,
						event: selectedEvent,
						selectedDate: selectedDate,
					}}
				/>
			)}
			{!calendarEventType &&
				(params?.diabetesAppointment ? (
					<AppointmentDetailModal
						patientId={patientId}
						opened={detailOpened}
						onClose={handleClose}
						selectedDate={selectedDate}
						defaultData={selectedEvent}
						workplaceId={workplaceId}
						params={params}
						onEventIsCreated={response => {
							onEventIsCreated?.(response);
							if (isNurseView) {
								setIsNurseConfirmModalOpened(true);
							}
						}}
					/>
				) : (
					<CalendarPatientEventModal
						healthPlanCode={healthPlanCode}
						patientId={patientId}
						caseId={caseId}
						opened={detailOpened}
						onClose={handleClose}
						selectedDate={selectedDate}
						defaultData={selectedEvent}
						workplaceId={workplaceId}
						params={params}
						onEventIsCreated={response => {
							onEventIsCreated?.(response);
							if (isNurseView) {
								setIsNurseConfirmModalOpened(true);
							}
						}}
					/>
				))}
			{isNurseConfirmModalOpened && (
				<HealthPlanCreateConfirmModal
					isOpen={isNurseConfirmModalOpened}
					patientId={patientId ?? ''}
					flyerType={Flyer.MEDDI_BABY_LATAM}
					title='component.healthPlanBabyCalendarNurseConfirmation.title'
					imageSource='/dist/icons/v2/check-green.svg'
					text={
						t('component.healthPlanBabyCalendarNurseConfirmation.text') ?? ''
					}
					onClose={() => {
						setIsNurseConfirmModalOpened(false);
					}}
				/>
			)}
		</>
	);
}
